<script lang="ts">
    import { onDestroy, tick } from 'svelte'
    import { derived as sderived, writable } from 'svelte/store'
    import type { Readable, Writable } from 'svelte/store'

    import Icon from '@iconify/svelte'
    import { isEqual, orderBy } from 'lodash-es'

    import { gettext as _, tgettext } from 'hydria/services/gettext'
    import { lg } from 'hydria/services/language'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch } from 'hydria/services/session'
    import { sgoto, spath, sreplace } from 'hydria/svelte_helpers'
    import { toLuxon } from 'hydria/utils/time'

    import { recordSortArray } from 'wdom/zone/zone'
    import type { EditableRecord, NewEditableRecord, ZoneTemplate } from 'wdom/zone/zone'
    import { Breadcrumb } from 'breadcrumb'

    import RecordEdit from 'wdom/zone/Zone/RecordEdit.svelte'
    import Records from 'wdom/zone/Zone/Records.svelte'

    interface Props {
        route: any
    }

    let { route }: Props = $props()

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    let templateNameElement: { fr: HTMLInputElement | null; en: HTMLInputElement | null } = $state({ fr: null, en: null })

    const crumbs = [{ txt: tgettext('Zone templates'), url: spath('/zone-templates') }, { txt: tgettext('Edit template') }]

    // Bunch of context to be "compatible" with Zone components..
    const status: Writable<'loading' | 'ready' | 'not-found' | 'error'> = writable('loading')
    const busy: Writable<boolean | 'saving' | 'updating' | 'removing'> = writable(true)
    const zoneUnicode: Readable<string> = sderived(lg, ($lg) => ($lg == 'fr' ? 'domaine.tpl' : 'domain.tpl'))

    const records: Writable<EditableRecord[]> = writable([])
    const zone: Writable<ZoneTemplate | undefined> = writable(undefined)
    const zoneEdit: Writable<ZoneTemplate | undefined> = writable(undefined)
    const recordErrors: Writable<{ [key: string]: string }> = writable({})
    let hasErrors = $derived(Object.keys($recordErrors).length > 0)
    let unsavedChanges = $derived(!isEqual($zone, $zoneEdit))

    let inId = $derived(route.result.path.params?.id)
    let isNew = $derived(inId === 'new')

    let nRecordsToSave = $derived($records.filter((r) => r.status != 'remove').length)

    let focussed = $state(false)
    $effect(() => {
        if ($status == 'ready' && $zoneEdit && !focussed) {
            focussed = true
            for (const l of ['fr', 'en']) {
                if ($zoneEdit[`name_${l}`] == '') {
                    tick().then(() => {
                        templateNameElement[l].focus()
                    })
                    break
                }
            }
        }
    })

    async function load() {
        zone.set(undefined)
        status.set('loading')

        try {
            const r = await simpleFetch('GET', `/json/zone/1/zone-template/${inId}`)
            status.set('ready')
            importServerData(r.data)
        } catch (e) {
            console.error('Error loading template:', e)
            status.set('error')
        }
        busy.set(false)
    }
    function loadNew() {
        importServerData({
            item: {
                name_fr: '',
                name_en: '',
                type: 'global',
                public: false,
                records: [],
            },
        })
        status.set('ready')
        busy.set(false)
    }
    // svelte-ignore state_referenced_locally
    if (inId == 'new') loadNew()
    else load()

    function importServerData(data: any) {
        for (const key of ['ctime', 'mtime']) {
            if (data.item[key]) data.item[key] = toLuxon(data.item[key])
        }

        if (data.item.public == null) data.item.public = false

        // Simplifies checks when comparing with $zoneEdit.records
        // to detect changes
        data.item.records = orderBy(data.item.records, [(rec) => recordSortArray('zone.tpl', rec)])

        zone.set(data.item)

        let eds: EditableRecord[] = []
        for (const record of data.item.records) {
            eds.push({
                saved: record,
                edited: { ...record },
                status: 'saved',
                hasError: false,
            })
        }
        eds = orderBy(eds, [(ed) => recordSortArray('zone.tpl', ed)], ['asc'])

        const zedit = { ...data.item }
        zedit.records = [...data.item.records]
        zoneEdit.set(zedit)
        records.set(eds)
    }

    let scrollY: number = $state(0)

    // #####################################################  Add a record

    let addRecordGroup: NewEditableRecord | undefined = $state()

    function closeAddRecord() {
        if (addRecordGroup == undefined) return
        if (addRecordGroup.edited.type != '') {
            records.update((v) => {
                v.unshift(addRecordGroup as EditableRecord)

                return orderBy(
                    v,
                    [
                        (ed) => {
                            return recordSortArray('zone.tpl', ed)
                        },
                    ],
                    ['asc'],
                )
            })
        }
        addRecordGroup = undefined
    }

    // Re-sort the records in case something changed (like 'idna'),
    // and cause a propagation on the store.
    function recordsChanged() {
        records.update((v) => {
            return orderBy(
                v,
                [
                    (ed) => {
                        return recordSortArray('zone.tpl', ed)
                    },
                ],
                ['asc'],
            )
        })
    }

    // #####################################################  Helpers / Validation

    let recToZoneEditCount = 0
    unsubs.push(
        records.subscribe((recs) => {
            if ($status == 'ready' && recToZoneEditCount == 0) {
                recToZoneEditCount++
                const zedit = $zoneEdit
                if (zedit) {
                    zedit.records = $records.filter((r) => r.status != 'remove').map((r) => r.edited)

                    simpleZoneValidation(zedit)

                    zoneEdit.set(zedit)

                    recToZoneEditCount--
                }
            }
        }),
    )

    function simpleZoneValidation(zoneEdit_: ZoneTemplate) {
        // Perform a very simple zone validation. For now:
        //  TXT: max 255 bytes (encoded in UTF-8, so we should encode to UTF-8 too)
        //  CNAME: can only have one CNAME per name
        //  DNAME: can only have one DNAME per name
        //  CNAME + DNAME: cannot be on same name
        //  CNAME: cannot have a non-cname on same name
        const rErrors = {}
        const names = {
            // There’s always "SOA" on ''
            '': { CNAME: 0, DNAME: 0, other: 1 },
        }

        for (const record of zoneEdit_.records) {
            if (!names[record.idna]) {
                names[record.idna] = { CNAME: 0, DNAME: 0, other: 0 }
            }
            if (names[record.idna][record.type] == null) names[record.idna].other++
            else names[record.idna][record.type]++
        }

        for (const [idna, counts] of Object.entries(names)) {
            if (counts.CNAME && counts.DNAME) {
                rErrors[idna] = tgettext('CNAME and DNAME cannot be used together')
            } else if (counts.CNAME && counts.other) {
                rErrors[idna] = tgettext('CNAME cannot be mixed with other types')
            } else if (counts.CNAME > 1) {
                rErrors[idna] = tgettext('There can only be one CNAME')
            } else if (counts.DNAME > 1) {
                rErrors[idna] = tgettext('There can only be one DNAME')
            }
        }

        const idnaInError = Object.keys(rErrors)
        for (const record of $records) {
            record.hasError = idnaInError.includes(record.edited.idna)
        }

        recordErrors.set(rErrors)
    }

    async function save() {
        if ($busy || hasErrors || !unsavedChanges || nRecordsToSave < 1 || $zoneEdit == null || $zone == null) return

        const data = {
            data: {
                name_fr: $zoneEdit.name_fr,
                name_en: $zoneEdit.name_en,
                type: $zoneEdit.type,
                public: $zoneEdit.public,
            },
            records: $zoneEdit.records,
        }
        busy.set('saving')
        try {
            const r = await simpleFetch('POST', isNew ? '/json/zone/1/zone-template' : `/json/zone/1/zone-template/${$zone.id}`, data)

            importServerData(r.data)
            notify({
                type: 'success',
                title: $_('Zone template'),
                text: $_('The information has been saved successfully.'),
            })
            if (isNew) {
                sreplace(`/zone-template/${r.data.item.id}`)
            }
        } catch (e: any) {
            notify({
                type: 'danger',
                title: $_('Save'),
                text: $_('Failed to validate the template: {error}', { error: e && e.data && e.data.error ? e.data.error : '?' }),
            })
        }

        busy.set(false)
    }

    async function remove() {
        if ($busy || unsavedChanges || $zone == null) return

        busy.set('removing')
        try {
            await simpleFetch('DELETE', `/json/zone/1/zone-template/${$zone.id}`)

            sgoto('/zone-templates')
            notify({
                type: 'success',
                title: $_('Zone template'),
                text: $_('The template has been removed.'),
            })
        } catch (e) {
            busy.set(false)
            console.error('Failed to remove the template:', e)
        }
    }
</script>

<style>
    .progress {
        --bs-progress-height: 2.184375rem;
        --bs-progress-font-size: 0.873rem;
    }

    .col-content {
        margin-right: 280px;
        display: grid;
        gap: 2rem;
    }
    .ui-scrollfix.col-save {
        position: fixed;
        right: 30px;
        top: 68px;
        bottom: 10px;
        overflow: auto;

        /* height: calc(100vh - 80px - 2rem); */
        height: auto;
        bottom: 30px;
    }
    .col-save {
        position: absolute;
        /* Fix le modal à l’intérieur de .col-save, moins de trouble que de portaller */
        z-index: 1;
        top: 0;
        right: 0;
        width: 250px;

        display: flex;
        flex-direction: column;
        gap: 1.5rem;

        height: calc(100vh - 80px - 2rem - 65px);
    }

    @media (max-width: 767px) {
        .col-content {
            margin-right: 0;
        }
        .ui-scrollfix.col-save {
            position: relative;
            width: 100%;
            top: 0;
            right: 0;
        }
        .col-save {
            position: relative;
            width: 100%;
            top: 0;
        }
    }
</style>

<Breadcrumb {crumbs} />

<svelte:window bind:scrollY />

{#if $status == 'not-found' || $status == 'error'}
    <div class="alert alert-danger">
        <Icon inline icon="fa:exclamation-triangle" />
        {#if $status == 'not-found'}
            {$_('The requested document does not exist.')}
        {:else}
            {$_('An error occured. Please try again in a moment.')}
        {/if}
    </div>
{:else if $zoneEdit != null}
    <div id="container-zone" class="status-{$status}" style="position: relative">
        <div class="col-content">
            <div class="card">
                <div class="card-title text-primary">{$_('Template configuration')}</div>
                <div class="card-body">
                    <form class="d-grid gap-2">
                        {#if $status == 'ready'}
                            <div class="row">
                                <label for="template-name_fr" class="col-sm-3 col-lg-2 col-form-label text-end">{$_('Nom (fr)')}</label>
                                <div class="col-sm-6 col-lg-5">
                                    <input type="text" class="form-control" id="template-name_fr" bind:value={$zoneEdit.name_fr} bind:this={templateNameElement.fr} disabled={$status !== 'ready'} class:is-invalid={!$zoneEdit.name_fr && $status == 'ready'} />
                                </div>
                            </div>
                            <div class="row">
                                <label for="template-name_en" class="col-sm-3 col-lg-2 col-form-label text-end">{$_('Nom (en)')}</label>
                                <div class="col-sm-6 col-lg-5">
                                    <input type="text" class="form-control" id="template-name_en" bind:value={$zoneEdit.name_en} bind:this={templateNameElement.en} disabled={$status !== 'ready'} class:is-invalid={!$zoneEdit.name_en && $status == 'ready'} />
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-sm-6 col-lg-5 offset-sm-3 offset-lg-2">
                                    <div class="checkbox">
                                        <label for="template-public">
                                            <input type="checkbox" id="template-public" bind:checked={$zoneEdit.public} />
                                            {$_('Visible et applicable par les clients')}
                                        </label>
                                    </div>
                                </div>
                            </div>
                        {/if}
                    </form>
                </div>
            </div>

            <Records {zone} {status} {records} {busy} {zoneUnicode} {zoneEdit} recordsChanged={() => recordsChanged()} editRecord={() => {}} />
        </div>

        <div class="col-save" class:ui-scrollfix={scrollY >= 80}>
            <div class="card card-cart">
                <div class="card-title text-primary">{$_('Template')}</div>
                <div class="card-body d-grid gap-2">
                    {#if $status == 'ready'}
                        {#if !$busy}
                            <button type="button" class="btn btn-primary save btn-block" disabled={hasErrors || !!$busy || !$records.length || !unsavedChanges || nRecordsToSave < 1} onclick={() => save()}>{isNew ? $_('Create') : $_('Save')}</button>
                        {/if}
                        {#if !$busy && !isNew}
                            <button type="button" class="btn btn-danger save btn-block" disabled={!!$busy} onclick={() => remove()}>{$_('Delete')}</button>
                        {/if}
                        {#if $busy == 'saving' || $busy == 'removing'}
                            <div class="controls">
                                <div class="progress propress-striped active">
                                    <div class="progress-bar" style="width: 100%" class:progress-bar-danger={$busy == 'removing'}>
                                        <span>{$busy == 'saving' ? $_('Saving…') : $_('Removing…')}</span>
                                    </div>
                                </div>
                            </div>
                        {/if}
                    {/if}
                    {#if $status == 'ready'}
                        <button
                            type="button"
                            disabled={!!$busy}
                            class="btn btn-secondary"
                            class:btn-danger={!$busy && isNew && nRecordsToSave < 1}
                            onclick={() => {
                                addRecordGroup = {
                                    saved: { idna: '', type: '', ttl: null },
                                    edited: { idna: '', type: '', ttl: null },
                                    status: 'new',
                                    hasError: false,
                                }
                            }}>{$_('Add a record')}</button
                        >
                    {/if}
                </div>
            </div>
        </div>
    </div>
    {#if addRecordGroup != null}
        <RecordEdit rg={addRecordGroup} close={() => closeAddRecord()} {status} {zoneUnicode} {zone} />
    {/if}
{/if}
